/*
 * rrweb 用户行为录制器
 *
 * 能力：
 * 1. 启动前读取服务端 /behaviorLog/config，支持总开关与指定用户录制
 * 2. 批量上报：达到条数阈值或定时器触发时上报，减少请求数
 * 3. 失败重试：上报失败写入 localStorage，下次启动重发（7 天过期）
 * 4. 自动暂停：长时间无操作暂停录制，用户恢复操作后开启新会话
 * 5. 页面隐藏/关闭前上报，尽可能避免数据丢失
 * 6. 回放页面自身不录制，避免录制数据自我累积
 */
import { getBehaviorConfig, reportBehaviorLog } from '@/API/behaviorLog';

const SESSION_KEY = 'behavior_record_session_id';
const BACKUP_PREFIX = 'behavior_backup_';
const BACKUP_EXPIRE = 7 * 24 * 60 * 60 * 1000;
const MAX_BACKUP_COUNT = 3;
const MAX_BACKUP_BYTES = 600 * 1024;
// 单次上报的事件 JSON 字符上限，需小于服务端 behavior.log.max-event-payload-length
const MAX_BATCH_CHARS = 400 * 1024;
// 页面卸载时 keepalive 请求的字符上限（浏览器限制约 64KB）
const KEEPALIVE_MAX_CHARS = 60 * 1024;
// 单条事件超过该长度时不再尝试备份，避免占满 localStorage
const MAX_SINGLE_EVENT_CHARS = 900 * 1024;

const DEFAULT_MAX_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL = 10000;
const DEFAULT_INACTIVE_TIMEOUT = 180000;
const DEFAULT_MAX_EVENTS_PER_REQUEST = 1000;
const START_DELAY = 3000;
const USER_WAIT_RETRY = 5;
const USER_WAIT_INTERVAL = 3000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

const state = {
  started: false,
  stopFn: null,
  flushTimer: null,
  inactiveTimer: null,
  events: [],
  sessionId: '',
  batchSeq: 0,
  targetUsers: [],
  maxBatchSize: DEFAULT_MAX_BATCH_SIZE,
  maxEventsPerRequest: DEFAULT_MAX_EVENTS_PER_REQUEST,
  pausedByInactive: false,
  listenersBound: false,
  starting: false,
  flushing: false
};

function log () {
  if (window.console && console.log) {
    console.log.apply(console, ['[BehaviorRecorder]'].concat(Array.prototype.slice.call(arguments)));
  }
}

/**
 * 读取当前登录用户，用户信息由 store 中的 getuser 动作写入
 */
export function getUserInfo () {
  try {
    const vm = window.vm;
    const user = vm && vm.$store && vm.$store.state && vm.$store.state.user;
    if (user) {
      return {
        userId: user.userId || '',
        userName: user.userName || ''
      };
    }
  } catch (e) {
    // 忽略读取异常
  }
  return { userId: '', userName: '' };
}

/**
 * 回放页与登录页不参与录制
 */
function isExcludedPage () {
  const path = window.location.pathname || '';
  if (path.indexOf('behaviorReplay') !== -1) {
    return true;
  }
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  const loginPattern = /(toLoginPage|rjAuthLoginPage|\/login)/i;
  return loginPattern.test(path) || loginPattern.test(search) || loginPattern.test(hash);
}

function createSessionId () {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId (renew) {
  if (renew) {
    state.sessionId = createSessionId();
    state.batchSeq = 0;
    try {
      sessionStorage.setItem(SESSION_KEY, state.sessionId);
    } catch (e) {
      // 忽略存储异常
    }
    return state.sessionId;
  }
  if (state.sessionId) {
    return state.sessionId;
  }
  try {
    state.sessionId = sessionStorage.getItem(SESSION_KEY) || createSessionId();
    sessionStorage.setItem(SESSION_KEY, state.sessionId);
  } catch (e) {
    state.sessionId = createSessionId();
  }
  return state.sessionId;
}

function parseTargetUsers (value) {
  if (!value) {
    return [];
  }
  return String(value)
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter((item) => !!item);
}

function isTargetUser (userId) {
  if (!state.targetUsers.length) {
    return true;
  }
  return state.targetUsers.indexOf(String(userId || '').toUpperCase()) >= 0;
}

function buildPayload (events) {
  const user = getUserInfo();
  return {
    sessionId: getSessionId(),
    batchSeq: state.batchSeq++,
    userId: user.userId,
    userName: user.userName,
    url: window.location.href,
    title: document.title,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    events
  };
}

function backupPayload (payload) {
  try {
    const text = JSON.stringify(payload);
    if (text.length > MAX_BACKUP_BYTES || text.length > MAX_SINGLE_EVENT_CHARS) {
      log('单批数据过大，跳过本地备份');
      return;
    }
    const keys = listBackupKeys();
    if (keys.length >= MAX_BACKUP_COUNT) {
      localStorage.removeItem(keys[0]);
    }
    localStorage.setItem(`${BACKUP_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, text);
  } catch (e) {
    log('本地备份失败', e);
  }
}

function listBackupKeys () {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.indexOf(BACKUP_PREFIX) === 0) {
        keys.push(key);
      }
    }
  } catch (e) {
    // 忽略存储异常
  }
  return keys.sort();
}

function cleanOldBackups () {
  const now = Date.now();
  listBackupKeys().forEach((key) => {
    const stamp = Number(key.replace(BACKUP_PREFIX, '').split('_')[0]);
    if (!stamp || now - stamp > BACKUP_EXPIRE) {
      localStorage.removeItem(key);
    }
  });
}

/**
 * 重发本地备份的历史数据
 */
export async function retryFailedLogs () {
  const keys = listBackupKeys();
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    try {
      const text = localStorage.getItem(key);
      if (!text) {
        continue;
      }
      const payload = JSON.parse(text);
      if (!payload || !payload.events || !payload.events.length) {
        localStorage.removeItem(key);
        continue;
      }
      await reportBehaviorLog(payload);
      localStorage.removeItem(key);
    } catch (e) {
      // 仍然失败，保留备份等待下次重试
      break;
    }
  }
}

/**
 * 按事件序列化长度取出一个上报批次，同时受条数与字符数限制
 */
function takeBatch (maxCount, maxChars) {
  const batch = [];
  let size = 0;
  while (state.events.length && batch.length < maxCount) {
    let length = 0;
    try {
      length = JSON.stringify(state.events[0]).length + 1;
    } catch (e) {
      length = 1;
    }
    // 保证至少取一条，避免超大快照事件无法上报
    if (batch.length && size + length > maxChars) {
      break;
    }
    batch.push(state.events.shift());
    size += length;
  }
  return batch;
}

/**
 * 上报缓冲区中的事件，按大小分片，避免单次请求超过服务端限制
 */
export async function flush (options = {}) {
  if (!state.events.length) {
    return;
  }
  if (state.flushing && !options.keepalive) {
    return;
  }
  state.flushing = true;
  const keepalive = !!options.keepalive;
  const maxChars = keepalive ? KEEPALIVE_MAX_CHARS : MAX_BATCH_CHARS;
  let guard = 0;
  try {
    while (state.events.length && guard < 50) {
      guard++;
      const events = takeBatch(state.maxBatchSize, maxChars);
      if (!events.length) {
        break;
      }
      const payload = buildPayload(events);
      let bodySize = 0;
      try {
        bodySize = JSON.stringify(payload).length;
      } catch (e) {
        bodySize = 0;
      }
      // 浏览器对 keepalive 请求体有 64KB 限制，超出时降级为普通请求
      const useKeepalive = keepalive && bodySize <= KEEPALIVE_MAX_CHARS;
      try {
        await reportBehaviorLog(payload, { keepalive: useKeepalive });
      } catch (e) {
        backupPayload(payload);
      }
      // 页面卸载场景只尽力发送一次，不阻塞卸载
      if (keepalive) {
        return;
      }
    }
  } finally {
    state.flushing = false;
  }
}

function handleEmit (event) {
  if (!state.started || isExcludedPage()) {
    return;
  }
  resetInactiveTimer();
  state.events.push(event);
  if (state.events.length >= state.maxBatchSize) {
    flush();
  }
}

function stopRecording () {
  if (state.stopFn) {
    try {
      state.stopFn();
    } catch (e) {
      // 忽略停止异常
    }
    state.stopFn = null;
  }
}

async function startRecording () {
  if (state.stopFn || state.starting) {
    return;
  }
  state.starting = true;
  try {
    const results = await Promise.all([
      import(/* webpackChunkName: "rrweb" */ 'rrweb'),
      import(/* webpackChunkName: "rrweb" */ '@rrweb/packer')
    ]);
    const mod = results[0];
    const packer = results[1];
    const record = mod.record || (mod.default && mod.default.record);
    if (!record) {
      log('rrweb 加载失败，行为录制不启动');
      return;
    }
    state.stopFn = record({
      emit: handleEmit,
      // 压缩事件后再上报，显著降低存储与服务端包体压力
      packFn: packer.pack,
      // 输入内容按需保留，仅密码框强制脱敏
      maskAllInputs: false,
      maskInputOptions: {
        password: true
      },
      // 添加 class="no-record" 的元素不被录制
      ignoreClass: 'no-record',
      sampling: {
        mousemove: 50,
        mouseInteraction: true,
        scroll: 150,
        input: 'last'
      },
      recordCanvas: false,
      collectFonts: false,
      recordCrossOriginIframes: false
    });
  } catch (e) {
    log('行为录制启动失败', e);
  } finally {
    state.starting = false;
  }
}

function handleInactive () {
  flush();
  stopRecording();
  state.pausedByInactive = true;
}

function resetInactiveTimer () {
  clearTimeout(state.inactiveTimer);
  state.inactiveTimer = setTimeout(handleInactive, DEFAULT_INACTIVE_TIMEOUT);
}

function onActivity () {
  if (!state.pausedByInactive) {
    return;
  }
  state.pausedByInactive = false;
  getSessionId(true);
  startRecording();
}

function bindListeners () {
  if (state.listenersBound) {
    return;
  }
  state.listenersBound = true;

  ACTIVITY_EVENTS.forEach((name) => {
    document.addEventListener(name, onActivity, true);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // 页面仍然存活，使用普通请求，避免 keepalive 的 64KB 体量限制
      flush();
      stopRecording();
    } else if (state.started && !state.stopFn) {
      startRecording();
    }
  });

  const flushOnLeave = () => flush({ keepalive: true });
  window.addEventListener('pagehide', flushOnLeave);
  window.addEventListener('beforeunload', flushOnLeave);
}

function startFlushTimer () {
  clearInterval(state.flushTimer);
  state.flushTimer = setInterval(() => {
    flush();
  }, DEFAULT_FLUSH_INTERVAL);
}

async function waitForUser () {
  if (!state.targetUsers.length) {
    return true;
  }
  for (let i = 0; i < USER_WAIT_RETRY; i++) {
    const user = getUserInfo();
    if (user.userId) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, USER_WAIT_INTERVAL));
  }
  return false;
}

async function bootstrap () {
  cleanOldBackups();
  let config = null;
  try {
    config = await getBehaviorConfig();
  } catch (e) {
    log('获取录制配置失败，不启动录制', e);
    return;
  }
  if (!config || !config.enabled) {
    log('录制功能已关闭');
    return;
  }
  state.targetUsers = parseTargetUsers(config.targetUsers);
  const maxEvents = Number(config.maxEventsPerRequest);
  if (maxEvents > 0) {
    state.maxEventsPerRequest = maxEvents;
    state.maxBatchSize = Math.min(DEFAULT_MAX_BATCH_SIZE, maxEvents);
  }

  if (!(await waitForUser())) {
    log('等待用户信息超时，不启动录制');
    return;
  }

  const user = getUserInfo();
  if (!isTargetUser(user.userId)) {
    log('当前用户不在录制范围内，跳过录制');
    return;
  }

  state.started = true;
  getSessionId();
  await startRecording();
  startFlushTimer();
  bindListeners();
  retryFailedLogs();
  log('行为录制已启动', user.userId);
}

/**
 * 初始化录制，延迟等待登录信息就绪
 */
function init (delay) {
  if (isExcludedPage()) {
    return;
  }
  setTimeout(bootstrap, typeof delay === 'number' ? delay : START_DELAY);
}

export default {
  init,
  flush,
  retryFailedLogs,
  getUserInfo,
  getSessionId
};
